import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { auth, firestore } from '../../Firebase';
import Login from './Login';
import TicketList from './TicketList';
import Ticket from './Ticket';
import CreateTicket from './CreateTicket';

const ROLE_BLOCKED_MESSAGES = {
  superAdmin: "Super Admin accounts cannot access support on this website. Please use a customer account.",
  admin: "Admin accounts cannot access support on this website. Please use a customer account.",
  logCompanyAdmin: "Logistic Company Admin accounts cannot access support on this website. Please use a customer account.",
  lcDriver: "LC driver accounts cannot access support on this website. Please use a customer account.",
  trippieDriver: "Trippie driver accounts cannot access support on this website. Please use a customer account.",
};

const BlockedView = ({ message }) => (
  <div className="login-container">
    <div className="login-box">
      <div className="login-form">
        <div className="login-message" style={{ marginBottom: '1rem' }}>
          {message}
        </div>
        <div className="login-button">
          <button className="standard-button" onClick={() => auth.signOut()}>Sign out</button>
        </div>
      </div>
    </div>
  </div>
);

/** Returns true if user is a Logistic Company Admin (in user_admin with company). Used when token claims are not yet set. */
async function isLogCompanyAdminFromFirestore(uid) {
  try {
    const doc = await firestore.collection('user_admin').doc(uid).get();
    return doc.exists && doc.data() && !!doc.data().company;
  } catch {
    return false;
  }
}

/** Returns 'lcDriver' | 'trippieDriver' | null */
async function getDriverRole(uid) {
  const doc = await firestore.collection('user_private').doc(uid).get();
  if (!doc.exists || !doc.data() || doc.data().hasDriverProfile !== true) return null;
  return doc.data().companyId ? 'lcDriver' : 'trippieDriver';
}

const SupportDashboard = () => {
  const { mode, ticketId } = useParams();

  const [user, setUser] = useState(null);
  const [blockedMessage, setBlockedMessage] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (!authUser || !authUser.emailVerified) {
        setUser(null);
        setBlockedMessage(null);
        return;
      }
      try {
        const tokenResult = await authUser.getIdTokenResult();
        const claims = tokenResult.claims || {};
        if (claims.superAdmin) {
          setUser(null);
          setBlockedMessage(ROLE_BLOCKED_MESSAGES.superAdmin);
          return;
        }
        // Logistic Company Admin only: user must be in user_admin with company (not token - Company Customers may have companyId)
        if (await isLogCompanyAdminFromFirestore(authUser.uid)) {
          setUser(null);
          setBlockedMessage(ROLE_BLOCKED_MESSAGES.logCompanyAdmin);
          return;
        }
        if (claims.admin) {
          setUser(null);
          setBlockedMessage(ROLE_BLOCKED_MESSAGES.admin);
          return;
        }
        const driverRole = await getDriverRole(authUser.uid);
        if (driverRole) {
          setUser(null);
          setBlockedMessage(ROLE_BLOCKED_MESSAGES[driverRole]);
          return;
        }
        setUser(authUser);
        setBlockedMessage(null);
      } catch {
        setUser(null);
        setBlockedMessage(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const getContent = () => {
    switch (mode) {
      case 'create-ticket':
        return <CreateTicket />;
      case 'ticket':
        return <Ticket ticketId={ticketId} userId={user.uid} />;
      default:
        return <TicketList user={user} />;
    }
  };

  if (blockedMessage) return <BlockedView message={blockedMessage} />;
  return user ? getContent() : <Login />;
};

export default SupportDashboard;